/**
 * Manual review of items' checks. The checks Morcast cannot decide alone, for the admin team.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client.js";

/** One run of a model check: what the model answered, and why it does not count, if it does not. */
export interface ModelRun<Answer> {
  answer: Answer | null;
  problem: string | null;
}

/** A frame of a video's upload, by an address valid for half an hour. */
export interface ReviewFrame {
  at: number;
  /** Its time in the video, "mm:ss". */
  label: string;
  /** Why it was taken: the declared segment, a drawn point, or the spread over the video. */
  purpose: "segment" | "drawn" | "spread";
  url: string;
  /** The public video at the same time. */
  watchUrl: string;
}

/** A model gate's record: its outcome, and each run with what it found. */
export interface GateRecord {
  outcome: "PASS" | "FAIL" | "REVIEW";
  problem?: string;
  runs?: ModelRun<{
    offTopic?: string[];
    verdict?: "met" | "not_met" | "uncertain";
    quote?: string | null;
    timestamp?: string | null;
    frame?: string | null;
    reason?: string;
    explanation?: string;
  }>[];
  /** DEDICATED: each run's seconds about the brand, of the video's. */
  coverages?: ({ covered: number; total: number } | null)[];
}

/** A check waiting for a person, as `GET /api/admin/reviews` describes it. */
export interface Review {
  submissionId: number;
  check:
    | "edits"
    | "rules"
    | "elements"
    | "media"
    | "format"
    | "manipulation"
    | "semantic"
    | "visual"
    | "fraud";
  /** The automatic check's evidence. */
  details: {
    /** Edits: what each read showed, for a post or a video. */
    reads?: {
      at: "end" | "day3";
      versionId?: string | null;
      compared: boolean;
      change: {
        text?: { before: string; after: string };
        title?: { before: string; after: string };
        description?: { before: string; after: string };
        durationSec?: { before: number; after: number };
        linksAdded: string[];
        linksRemoved: string[];
      } | null;
    }[];
    /** Media match: the upload's duration against the published video's. */
    uploadDurationMs?: number;
    capturedDurationSec?: number;
    /** Format: the share of the video that holds speech, and the format's model gate. */
    speech?: { speechMs: number; durationMs: number; silent: boolean };
    coverage?: GateRecord;
    segment?: GateRecord;
    /** Why Morcast could not decide, such as an unreadable capture. */
    problem?: string;
    /** Manipulation audit: each run, with the excerpts it found aimed at the checker. */
    runs?: ModelRun<{ attempt: boolean; quotes: string[]; explanation: string }>[];
    /** Semantic and visual checks. Each requirement's outcome and runs. */
    requirements?: {
      requirement: string;
      outcome: "PASS" | "FAIL" | "REVIEW";
      runs?: ModelRun<{
        verdict: "met" | "not_met" | "uncertain";
        quote?: string | null;
        frame?: string | null;
        reason: string;
      }>[];
      problem?: string;
    }[];
    /** Semantic check: each thing the brief prohibits, with what each run found. */
    prohibitions?: {
      prohibition: string;
      outcome: "PASS" | "FAIL" | "REVIEW";
      runs?: ModelRun<{
        verdict: "found" | "not_found" | "uncertain";
        quote?: string | null;
        frame?: string | null;
        reason: string;
      }>[];
    }[];
    /** Fraud: the item's metric, from what it is reviewed, and on what basis. */
    metric?: string;
    reviewFrom?: string;
    rule?: "baseline" | "new_account" | "no_posts" | "no_history";
    median?: string;
    posts?: number;
    days?: number;
    truncated?: boolean;
    threshold?: string;
    accountCreatedAt?: string;
    missingHistory?: string;
    window?: { from: string; to: string };
  };
  /** When Morcast's check asked for a person. */
  since: string;
  submission: {
    url: string;
    contentId: string | null;
    accountId: string | null;
    wallet: string;
    status: string;
    postedAt: string | null;
    receivedAt: string;
    /** INTEGRATION: the declared segment, in seconds. */
    segment: { start: number; end: number } | null;
  };
  /** A YouTube item: what the media service found in the upload. */
  video: {
    watchUrl: string;
    analysed: "analyzed" | "failed" | null;
    transcript: { start: number; end: number; text: string }[] | null;
    /** Which model made the transcript, and where. */
    transcriber: string | null;
    frames: ReviewFrame[];
  } | null;
  campaign: {
    chainId: number;
    escrow: string;
    id: string;
    title: string;
    brandName: string;
    platform: "X" | "YOUTUBE";
    format: "DEDICATED" | "INTEGRATION" | null;
    /** The edits the brief allows. */
    allowedEdits: string;
    endAt: string;
    /** The brand kit, which the visual checks showed the model as references. */
    assets: { label: string; url: string }[];
  };
}

const REVIEWS_KEY = ["admin-reviews"] as const;

/** The checks waiting for a person, oldest first. */
export function useReviews() {
  return useQuery({
    queryKey: REVIEWS_KEY,
    queryFn: async () => (await api.get<{ reviews: Review[] }>("/api/admin/reviews")).reviews,
  });
}

/** Decides a check: pass, or fail with the check's reason. The note says why. */
export function useDecideReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      submissionId: number;
      check: Review["check"];
      outcome: "pass" | "fail";
      note: string;
    }) =>
      api.post(`/api/admin/reviews/${input.submissionId}/${input.check}`, {
        outcome: input.outcome,
        note: input.note,
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: REVIEWS_KEY }),
  });
}
